import { X, ExternalLink, Lock } from "lucide-react";
import CoverArtUpload from "./CoverArtUpload";

/**
 * EntryDetailModal — shows every field of an entry, no truncation.
 * Resolves artist IDs → names, release IDs → titles, URL fields become
 * clickable "Open" links, "Other" values show their specify text inline.
 */
export default function EntryDetailModal({ entry, fields, title, onClose, artistMap = {}, releaseMap = {}, user, department, onCoverChange }) {
  if (!entry) return null;
  const data = entry.data || {};

  return (
    <div
      onClick={onClose}
      data-testid="entry-detail-backdrop"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1100, padding: 20, overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="panel modal-content"
        data-testid="entry-detail-modal"
        style={{
          padding: 0, width: "100%", maxWidth: 640,
          maxHeight: "88vh", overflowY: "auto",
          margin: "auto",
        }}
      >
        {/* Sticky header — the close button and title stay visible even
            while scrolling long entries. */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
          padding: "20px 26px 14px",
          position: "sticky", top: 0, zIndex: 2,
          background: "var(--panel)",
          borderBottom: "1px solid var(--border)",
          borderTopLeftRadius: "inherit", borderTopRightRadius: "inherit",
        }}>
          <div>
            <div className="muted" style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>Entry Details</div>
            <h3 style={{ fontSize: 18, margin: 0 }}>{title}</h3>
          </div>
          <button onClick={onClose} className="btn" data-testid="close-entry-detail" aria-label="Close" style={{ padding: "6px 8px" }}>
            <X size={14} />
          </button>
        </div>

        <div style={{ padding: "18px 26px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
          {department === "released_songs" && user && (
            <div data-testid="detail-field-cover-art" style={{ borderBottom: "1px solid var(--border)", paddingBottom: 14 }}>
              <div className="muted" style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>Cover Art</div>
              <CoverArtUpload
                user={user}
                entryId={entry.id}
                coverPath={data.cover_art_path || ""}
                onChange={(newPath) => onCoverChange?.(entry.id, newPath)}
              />
            </div>
          )}
          {fields.map(f => {
            const rawVal = data[f.key];
            const otherVal = data[`${f.key}_other`];
            const isEmpty = rawVal == null || rawVal === "" || (Array.isArray(rawVal) && rawVal.length === 0);
            return (
              <div key={f.key} data-testid={`detail-field-${f.key}`} style={{ borderBottom: "1px solid var(--border)", paddingBottom: 12 }}>
                <div className="muted" style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, marginBottom: 5, display: "flex", alignItems: "center", gap: 6 }}>
                  {f.label}{f.locked && <Lock size={10} />}
                </div>
                <div style={{ fontSize: 14, lineHeight: 1.55, wordBreak: "break-word" }}>
                  {isEmpty ? (
                    <span className="muted">—</span>
                  ) : (
                    renderDetailValue(rawVal, f, { artistMap, releaseMap, otherVal })
                  )}
                </div>
              </div>
            );
          })}

          {data.status && (
            <div style={{ borderBottom: "1px solid var(--border)", paddingBottom: 12 }} data-testid="detail-field-status">
              <div className="muted" style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, marginBottom: 5 }}>Status</div>
              <span className="badge badge-brand" style={{ padding: "4px 10px", fontSize: 12 }}>{data.status}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function renderDetailValue(v, f, ctx) {
  const { artistMap, releaseMap, otherVal } = ctx;
  if (f.type === "url") {
    return (
      <a href={v} target="_blank" rel="noreferrer" style={{ color: "var(--indigo)", display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ overflowWrap: "anywhere" }}>{v}</span>
        <ExternalLink size={12} />
      </a>
    );
  }
  if (f.type === "number") {
    return typeof v === "number" ? v.toLocaleString("en-IN") : String(v);
  }
  if (f.type === "artistSelect") {
    return artistMap[v] || v;
  }
  if (f.type === "releaseSelect") {
    return releaseMap[v] || v;
  }
  if (f.type === "multiArtistSelect" && Array.isArray(v)) {
    const names = v.map(id => artistMap[id] || id).filter(Boolean);
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {names.map((n, i) => (
          <span key={i} className="badge badge-brand" style={{ padding: "4px 10px", fontSize: 12 }}>{n}</span>
        ))}
      </div>
    );
  }
  if (f.type === "select") {
    if (String(v) === "Other" && otherVal) {
      return <span><span className="muted">Other — </span>{otherVal}</span>;
    }
    return String(v);
  }
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}
